import { BigmlAnswersDTO, IBigmlAnswersDTO } from "./BigmlAnswers.DTO";
import { EstadoProyectoEnum } from "../shared/enums/EstadoProyecto.enum";
import { BigmlAppsDTO, IBigmlAppsDTO } from "./BigmlApps.DTO";
import { Nilable } from "@shared/utils";

export interface IBigmlProyectoDTO {
    id: Nilable<number>;
    message: Nilable<string>;
    name: Nilable<string>;
    resource: Nilable<string>;
    predict_size: Nilable<number>;
    overall_tags: Nilable<string>;
    statistics_data: Nilable<IBigmlProyectoStatsDTO>;
    combo: Nilable<number>;
    train_dataset: Nilable<number>;
    final_dataset: Nilable<string>;
    created_with_bases: Nilable<boolean>;
    local_predicts: Nilable<boolean>;
    filter_with_logic: Nilable<boolean>;
    projects_count: Nilable<number>;
    state: Nilable<EstadoProyectoEnum>;
    is_split: Nilable<boolean>;
}

export class BigmlProyectoDTO {
    // TODO: agregar vectorized, pero no al toJSON()
    public id = 0;
    public message = "";
    public name = "";
    public resource = "";
    public predictSize = 0;
    public overallTags: Nilable<BigmlProyectoTagsDTO> = null;
    public statisticsData: Nilable<BigmlProyectoStatsDTO> = null;
    public combo = 0;
    public trainDataset = 0;
    public finalDataset = "";
    public createdWithBases = false;
    public localPredicts = false;
    public filterWithLogic = false;
    public creado = "";
    public selectedForDelete = false;
    public projectsCount = 0;
    public state = EstadoProyectoEnum.INITIALIZING; // default.
    public isSplit = false;

    constructor(json: Nilable<Partial<IBigmlProyectoDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IBigmlProyectoDTO>) {
        if (json.id != null) {
            this.id = json.id;
        }
        if (json.message != null) {
            this.message = json.message;
        }
        if (json.name != null) {
            this.name = json.name;
        }
        if (json.resource != null) {
            this.resource = json.resource;
        }
        if (json.predict_size != null) {
            this.predictSize = json.predict_size;
        }
        if (json.overall_tags != null) {
            this.overallTags = new BigmlProyectoTagsDTO(JSON.parse(json.overall_tags));
        }
        if (json.statistics_data != null) {
            this.statisticsData = new BigmlProyectoStatsDTO(json.statistics_data);
        }
        if (json.combo != null) {
            this.combo = json.combo;
        }
        if (json.train_dataset != null) {
            this.trainDataset = json.train_dataset;
        }
        if (json.final_dataset != null) {
            this.finalDataset = json.final_dataset;
        }
        if (json.created_with_bases != null) {
            this.createdWithBases = json.created_with_bases;
        }
        if (json.filter_with_logic != null) {
            this.filterWithLogic = json.filter_with_logic;
        }
        if (json.local_predicts != null) {
            this.localPredicts = json.local_predicts;
        }
        if (json.projects_count != null) {
            this.projectsCount = json.projects_count;
        }
        if (json.state != null) {
            this.state = json.state;
        }
        if (json.is_split != null) {
            this.isSplit = json.is_split;
        }
    }

    public toJSON(): IBigmlProyectoDTO {
        return {
            id: this.id,
            message: this.message,
            name: this.name,
            resource: this.resource,
            predict_size: this.predictSize,
            overall_tags: JSON.stringify(this.overallTags?.toJSON()),
            statistics_data: this.statisticsData?.toJson() ?? null,
            combo: this.combo,
            train_dataset: this.trainDataset,
            final_dataset: this.finalDataset,
            created_with_bases: this.createdWithBases,
            local_predicts: this.localPredicts,
            filter_with_logic: this.filterWithLogic,
            projects_count: this.projectsCount,
            state: this.state,
            is_split: this.isSplit
        };
    }
}

export interface IBigmlProyectoStatsDTO {
    total_train_users: Nilable<number>;
    total_predict_users: Nilable<number>;
    train_mc_questions: Nilable<number[]>;
    predict_mc_questions: Nilable<number[]>;
    train_answers: Nilable<IBigmlAnswersDTO>;
    train_apps: Nilable<IBigmlAppsDTO>;
    predict_answers: Nilable<IBigmlAnswersDTO>;
    predict_apps: Nilable<IBigmlAppsDTO>;
}

export class BigmlProyectoStatsDTO {
    public totalTrainUsers = 0;
    public totalPredictUsers = 0;
    public trainMultipleChoiceQuestions: number[] = [];
    public predictMultipleChoiceQuestions: number[] = [];
    public trainAnswers: Nilable<BigmlAnswersDTO> = null;
    public trainApps: Nilable<BigmlAppsDTO> = null;
    public predictAnswers: Nilable<BigmlAnswersDTO> = null;
    public predictApps: Nilable<BigmlAppsDTO> = null;

    constructor(json: Nilable<Partial<IBigmlProyectoStatsDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IBigmlProyectoStatsDTO>) {
        this.trainAnswers = new BigmlAnswersDTO(json.train_answers);
        this.trainApps = new BigmlAppsDTO(json.train_apps);
        this.predictAnswers = new BigmlAnswersDTO(json.predict_answers);
        this.predictApps = new BigmlAppsDTO(json.predict_apps);

        if (json.total_train_users != null) {
            this.totalTrainUsers = json.total_train_users;
        }
        if (json.total_predict_users != null) {
            this.totalPredictUsers = json.total_predict_users;
        }
        if (json.train_mc_questions != null) {
            this.trainMultipleChoiceQuestions = json.train_mc_questions;
        }
        if (json.predict_mc_questions != null) {
            this.predictMultipleChoiceQuestions = json.predict_mc_questions;
        }
    }

    public toJson(): IBigmlProyectoStatsDTO {
        return {
            total_train_users: this.totalTrainUsers,
            total_predict_users: this.totalPredictUsers,
            train_mc_questions: this.trainMultipleChoiceQuestions,
            predict_mc_questions: this.predictMultipleChoiceQuestions,
            train_answers: this.trainAnswers?.toJSON() ?? null,
            predict_answers: this.predictAnswers?.toJSON() ?? null,
            train_apps: this.trainApps?.toJSON() ?? null,
            predict_apps: this.predictApps?.toJSON() ?? null
        };
    }
}

export interface IBigmlProyectoTagsDTO {
    country: Nilable<string[]>;
    gender: Nilable<string[]>;
    age_range: Nilable<string[]>;
}

export class BigmlProyectoTagsDTO {
    public country: string[] = [];
    public gender: string[] = [];
    public ageRange: string[] = [];

    constructor(json: Nilable<Partial<IBigmlProyectoTagsDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IBigmlProyectoTagsDTO>) {
        if (json.country != null) {
            if (typeof json.country === "string") {
                this.country = [json.country];
            } else {
                this.country = json.country;
            }
        }
        if (json.gender != null) {
            this.gender = json.gender;
        }
        if (json.age_range != null) {
            this.ageRange = json.age_range;
        }
    }

    public toJSON(): IBigmlProyectoTagsDTO {
        return {
            country: this.country,
            gender: this.gender,
            age_range: this.ageRange
        };
    }
}
